import { atom } from 'infrastructure/state/jotai.wrapper'
import { doAdminQueries } from 'infrastructure/api'
import type { ResourceQuery } from 'infrastructure/api'
import { loadable } from 'jotai/utils'
import { GetUserRoleCount } from 'shared/api/organizations/organizations.utils'
import { Single } from 'infrastructure/api'
import { atomWithRefresh } from 'infrastructure/state/atomWithRefresh'

const queryKey = 'school'

export type SchoolCourse = {
  course_id: number
  curriculum_id: number
  deleted: boolean
  product_group_id: number
  product_id: number
  title: string
  tree_id: string
  version_id: string
}

type SchoolResponse = {
  id: number
  name: string
  address: string
  city: string
  state: string
  county: string
  zip_code: string
  country: string
  is_public: boolean
  is_verified: boolean
  school_searchable: string | null
  school_sis_id?: string
  features: Single.PlatformFeature[]
  district: { id: number; name: string }
  school_courses: SchoolCourse[]
  created_at: string
  deleted_at?: string
}

async function fetchSchool(id: number) {
  const resourceData: ResourceQuery = {
    key: queryKey,
    fields: [
      'id',
      'name',
      'address',
      'city',
      'state',
      'county',
      'zip_code',
      'country',
      'is_public',
      'is_verified',
      'school_searchable',
      'school_sis_id',
      'created_at',
      'deleted_at',
      { features: ['id', 'name', 'locked', 'free_trial'] },
      {
        school_courses: [
          'course_id',
          'curriculum_id',
          'title',
          'deleted',
          'product_id',
          'product_group_id',
          'tree_id',
          'version_id',
        ],
      },
      {
        district: ['id', 'name'],
      },
    ],
  }
  const params = { id }
  const response = await doAdminQueries<SchoolResponse>(
    [{ resourceData, params }],
    false,
  )
  return response.data?.[queryKey]
}
export const schoolIdAtom = atom<number | undefined>(undefined)

export const schoolResponseAtom = atomWithRefresh(async (get) => {
  const schoolId = get(schoolIdAtom)
  if (!schoolId) return
  return fetchSchool(schoolId)
})

const schoolUserCountsAtom = loadable(
  atom(async (get) => {
    const schoolId = get(schoolIdAtom)
    if (!schoolId)
      return {
        teacher_count: 0,
        admin_count: 0,
        student_count: 0,
      }
    return GetUserRoleCount(schoolId, 'school')
  }),
)

export const schoolUserCountsWithDefaultAtom = atom((get) => {
  const data = get(schoolUserCountsAtom)
  if (data.state !== 'hasData') {
    return {
      teacher_count: 0,
      admin_count: 0,
      student_count: 0,
    }
  }
  return data.data
})
